import { Invitation } from "myfitworld-model";
import React, { Fragment, useContext, useState } from "react";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Dialog, DialogTitle, DialogActions, makeStyles, createStyles, Typography, Link } from "@material-ui/core";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import InviteUserForm from "./InviteUserForm";
import useTableStyles from "../../theme/useTableStyles";
import Role from "myfitworld-model/dist/enums/Role";
import { useOrganizationProvider } from "../../providers/OrganizationProvider";
import SideDrawer from "../SideDrawer";
import LoadingSpinner from "../LoadingSpinner";
import globalState from "../../globalState";
import { useStoreState } from "pullstate";
import { revokeInvitationFunctions } from "../../api/invitationsApi";
import { useMfwThemeProvider } from "myfitworld-utils";
import globalMessages from '../../messages';
import invitationMessages from './messages';
import { FormattedMessage, useIntl } from 'react-intl';
import useOrganizationInvitations from "../../hooks/Organizations/useOrganizationInvitations";
import { useUserProvider } from "../../providers/UserProvider";
import UserContext from "src/contexts/UserContext";
import useAdminTrainerCountsAndRoles from "src/hooks/useAdminTrainerCountsAndRoles";
import { buildUrl, configMainRoutesConsts } from "src/config/route";
import { useNavigate } from "@reach/router";
import InvitationStatusCell from "./InvitationStatusCell";
import { getUserFullName } from "src/utils/getUserFullName";
import Alert from "@material-ui/lab/Alert";
import useNavigation from "src/hooks/useNavigation";
export const CALENDLY_LINK = "https://calendly.com/milan-sundac/myfitworld-introduction-clone-3";

interface Props {
  roles: Role[];
  showRole: boolean;
  createActionLabel: string;
  formHelperText?: string;
  invitationsBlocked?: boolean;
  setLoadingRevokeDisableCreateInvitationButton?: (a: boolean) => void,
  loadingRevokeDisableCreateInvitationButton?: boolean,
  disableFormIfQuantityAndCard?: () => void,
  disableInvitationForm?: boolean;
  bundleLimitMessage?: string;
  freeMaxLimitMessage?: string;
}
interface ConfirmationDialogState {
  isOpen: boolean;
  invitation: Invitation | undefined;
}

const useStyles = makeStyles(() =>
  createStyles({
    dialogActions: {
      display: 'flex',
      justifyContent: 'space-between'
    },
  }),
);

const InvitationsTable = (
  { roles, showRole,
    createActionLabel, formHelperText, invitationsBlocked,
    loadingRevokeDisableCreateInvitationButton,
    setLoadingRevokeDisableCreateInvitationButton,
    disableFormIfQuantityAndCard,
    disableInvitationForm,
    bundleLimitMessage,
    freeMaxLimitMessage,
  }: Props) => {
  const tableClasses = useTableStyles();
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const theme = useMfwThemeProvider();
  const [{ selectedOrganization }] = useOrganizationProvider();
  const role = useStoreState(globalState, s => s.currentRole);
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] = useState<ConfirmationDialogState>({ isOpen: false, invitation: undefined });
  const navigate = useNavigate();
  const { user: authUser } = useUserProvider();
  const { navigation } = useNavigation();
  const { organizationInvitations: invitations, isLoadingInvitationsList, fetchAgain, sendAgainInvitation } = useOrganizationInvitations({
    userId: authUser?.id,
    organizationId: authUser?.currentOrganization,
    roles: roles,
    role: role,
    isWhiteLabel: theme.theme.isWhiteLabel
  });
  const userContext = useContext(UserContext);
  const currentOrganization = useStoreState(globalState, s => s.currentOrganization);
  const currentOrganizationId = currentOrganization?.id || '';
  const { isArchivedUser: isArchivedLoginUser } = useAdminTrainerCountsAndRoles(currentOrganizationId, userContext?.user);

  const revokeInvitation = (invitationId: string, userId?: string) => {
    const isClient = roles.includes(Role.Client);

    setLoadingRevokeDisableCreateInvitationButton && setLoadingRevokeDisableCreateInvitationButton(true);

    revokeInvitationFunctions(invitationId, isClient, selectedOrganization?.id || "", userId, theme.theme.isWhiteLabel)
      .then((res: boolean) => {
        setLoadingRevokeDisableCreateInvitationButton && setLoadingRevokeDisableCreateInvitationButton(false);
        if (res) {
          disableFormIfQuantityAndCard && disableFormIfQuantityAndCard();
          fetchAgain();
        }
      })
  }

  const confirmationDialogAction = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isOpenConfirmationDialog.invitation?.id) {
      e.stopPropagation();
      revokeInvitation(isOpenConfirmationDialog.invitation?.id, isOpenConfirmationDialog.invitation.userId);
      setIsOpenConfirmationDialog({ isOpen: false, invitation: undefined });
    }
  }

    const goToBillingDetails = () => {
      localStorage.setItem("openBillingDetails", "1");
      navigation('/billing');
  };

  return (
    <Fragment>
      <Dialog
        open={isOpenConfirmationDialog.isOpen}
        onClose={() => setIsOpenConfirmationDialog({ isOpen: false, invitation: undefined })}
      >
        <DialogTitle>
          {formatMessage(invitationMessages.are_you_sure_you_want_to_remove, { name: getUserFullName({ firstName: isOpenConfirmationDialog.invitation?.firstName, lastName: isOpenConfirmationDialog.invitation?.lastName }) })}
        </DialogTitle>

        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => { setIsOpenConfirmationDialog({ isOpen: false, invitation: undefined }) }} color="secondary">{formatMessage(globalMessages.cancel)}</Button>
          <Button
            onClick={(e) => { confirmationDialogAction(e) }}
            color="primary"
            autoFocus
          >
            {formatMessage(globalMessages.yes)}
          </Button>
        </DialogActions>
      </Dialog>

      <div className={tableClasses.tableActionBarPending}>
        <Button
          color='primary'
          onClick={(event) => { event.stopPropagation(); setFormOpen(true) }}
          disabled={!!isArchivedLoginUser}
        >
          {formatMessage((invitationMessages as any)[createActionLabel])}
        </Button>
      </div>

      {loadingRevokeDisableCreateInvitationButton && <Box display="flex" alignItems="center"><LoadingSpinner marginTop={0} /></Box>}

      {isLoadingInvitationsList ? <LoadingSpinner marginTop={50} /> :
        invitations.length > 0 ?
          <TableContainer>
            <Table className={tableClasses.table} size="medium">
              <TableHead>
                <TableRow>
                  <TableCell align="left">{formatMessage(globalMessages.name)}</TableCell>
                  <TableCell align="left">{formatMessage(globalMessages.email)}</TableCell>
                  {showRole && <TableCell align="left">{formatMessage(globalMessages.role)}</TableCell>}
                  <TableCell align="left">{formatMessage(globalMessages.created_at)}</TableCell>
                  <TableCell align="center">{formatMessage(invitationMessages.status)}</TableCell>
                  <TableCell align="center">{formatMessage(globalMessages.actions)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.map((invitation) => (
                  <TableRow
                    key={invitation.id}
                    hover={!!(invitation.userExist && invitation.userId && !invitation.rejected)}
                    onClick={() => {
                      if (invitation.userExist && invitation.userId && !invitation.rejected) {
                        navigate(buildUrl(configMainRoutesConsts.clientFormContainerPage, [invitation.userId]));
                      }
                    }}
                  >
                    <TableCell align="left">{getUserFullName({ firstName: invitation.firstName, lastName: invitation.lastName })}</TableCell>
                    <TableCell align="left">{invitation.email || ''}</TableCell>
                    {showRole && <TableCell align="left">{formatMessage((invitationMessages as any)[(invitation as Invitation).role.toLowerCase()])}</TableCell>}
                    <TableCell align="left">{invitation.createdAt && formatDistanceToNow(new Date(invitation.createdAt), { addSuffix: true })}</TableCell>
                    <InvitationStatusCell
                      invitation={invitation}
                      sendAgainInvitation={sendAgainInvitation}
                    />
                    <TableCell align="center">
                      <Button
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsOpenConfirmationDialog({ isOpen: true, invitation: invitation as Invitation })
                        }}
                        disabled={loadingRevokeDisableCreateInvitationButton || invitationsBlocked}
                        color="secondary"
                      >
                        {formatMessage(globalMessages.remove)}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          :
          <Box display='flex' justifyContent='center' alignItems='center'>
            <Typography variant='h5' color='primary'>
              {createActionLabel === 'create_member' ? formatMessage(invitationMessages.dont_have_members) : formatMessage(invitationMessages.dont_have_invitations)}
            </Typography>
          </Box>
      }

      <SideDrawer isOpen={formOpen} onClose={() => { setFormOpen(false); }}>
        {disableInvitationForm ?
          <Alert severity='info' style={{margin: '24px auto 0 auto', maxWidth: 520, padding: 24}}>
            {bundleLimitMessage && 
              <>
                <Typography>{bundleLimitMessage}</Typography>
                <Typography>
                  <FormattedMessage
                    {...invitationMessages.upgrade_subscription}
                    values={{
                    manageSubscription: (
                      <Link onClick={goToBillingDetails} component='button' style={{fontSize: '16px', marginBottom: '3px'}}>
                        {formatMessage(invitationMessages.manage_your_subscription)}
                      </Link>
                    ),
                    schedule: (
                      <Link onClick={() => window.open(CALENDLY_LINK, '_blank')} component='button' style={{fontSize: '16px', marginBottom: '3px'}}>
                        {formatMessage(invitationMessages.schedule)}
                      </Link>
                    ),
                    }}
                  />
                </Typography>
              </>
            
            }
            {freeMaxLimitMessage && <Typography>{freeMaxLimitMessage}</Typography>}
          </Alert>
          :
          <InviteUserForm
            onSubmit={() => {
              setFormOpen(false);
              fetchAgain();
              disableFormIfQuantityAndCard && disableFormIfQuantityAndCard();
            }}
            roles={roles}
            createActionLabel={createActionLabel}
            helperText={formHelperText}
            invitationsBlocked={invitationsBlocked}
          />
        }
      </SideDrawer>
    </Fragment>
  );
};

export default InvitationsTable;
